import { useEffect, useState } from "react";
import Layout from "../../../Containers/Layout";
import styles from "./StudyProgramForm.module.scss";
import { Form, DatePicker, Select, Col, Row, Spin, Tooltip, InputNumber, Checkbox, Input } from "antd";
import CustomForm from "../../../CustomComponents/CustomForm";
import { theme } from "../../../theme";
import { Redirect, useParams } from "react-router-dom";
import {
  NOTIFICATION_TYPES,
  openNotification,
} from "../../Notifications/NotificationsUtils";
import ConfirmationModal from "../../../Containers/ConfirmationModal";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import useDebounce from "../../../Hooks/debounce";
import { getDomains } from "../../../Requests/domain-requests";
import { AccreditationTypeEnum, GeographicLocationEnum, LearningCompetence, LearningCompetenceTypeEnum, MasterCategoryEnum, StudyCycleEnum, StudyPlanStatusEnum, StudyProgramLangEnum, StudyProgramUpdateDTO, TeachingFormatEnum } from "../../../Api";
import { addStudyProgram, deleteLearningCompetance, getStudyProgram, updateStudyProgram } from "../../../Requests/study-program-request";
import moment from 'moment'
import { getFaculties } from "../../../Requests/faculty-requests";
import { dateFormat } from "../../../utils/constants";
import { getAllAcademicUsers } from "../../../Requests/academic-user-requests";
import ProgramCompetenceForm from "./ProgramCompetenceForm";
import { InfoCircleOutlined } from "@ant-design/icons";
import { isProgramSupervisor, useIsAdmin, useIsDirectorCsud } from "../../../utils/utilFunctions";
import { getAllStudyPlans, getStudyPlanById, updateStudyPlanInfo } from "../../../Requests/study-plan-requests";
import { getCompetenceTemplates } from "../../../Requests/competence-template-requests";
import { extractErrorPayload } from "../../../utils/extractErrorPayload";

const { Option } = Select;

const mapPlanLearningCompetence = (c: any): LearningCompetence => ({
  id: c.id,
  description: c.description,
  type: c.type,
  identificator: c.identificator,
  isDomainLearningOutcomeTemplate: c.isDomainLearningOutcomeTemplate,
  domainLearningOutcomeTemplateId: c.domainLearningOutcomeTemplateId,
  isCompetenceTemplate: c.isCompetenceTemplate,
  competenceTemplateId: c.competenceTemplateId,
  isCompetenceTemplateAssociatedOutcome: c.isCompetenceTemplateAssociatedOutcome,
  associatedResultTemplateId: c.associatedResultTemplateId
});


const StudyProgramForm = (props: any) => {
  const { t, i18n } = useTranslation();

  const openGetErrorNotification = (_error: any) => {
    openNotification(
      'Eroare',
      'Datele nu au putut fi aduse din server!',
      NOTIFICATION_TYPES.ERROR
    );
  };
  const [redirect, setRedirect] = useState(false);
  const [selectedDraftPlanId, setSelectedDraftPlanId] = useState<string | undefined>(undefined);

  const [searchTermProgramSupervisors, setProgramSupervisorsSearchTerm] = useState('');
	const debouncedProgramSupervisorsSearch = useDebounce(searchTermProgramSupervisors, 1000);
  const isAdmin = useIsAdmin();
  const isDirectorCsud = useIsDirectorCsud();
  const [form] = Form.useForm<{
    name: string | null,
    facultyId: string,
    domainId: string;
    programNumber: number;
    isDualProgram: boolean;
    partnerName: string | null;
    numberECTS: number,
    externalEvaluationHistory: string | null,
    accreditationType: AccreditationTypeEnum,
    isEurAceCertified: boolean,
    capacity: number,
    educaionalDomainISCED: string | null,
    codCOR: string | null,
    ocupationName: string | null,
    ancNoticeNumber: string | null,
    ancNoticeDate: Date,
    aracisNoticeDate: Date
    programSupervisor: string | null,
    language: StudyProgramLangEnum,
    masterCategory: MasterCategoryEnum,
    programSupervisorsIds: string[] | null;
    mission: string | null,
    objective: string | null,
    teachingFormat: TeachingFormatEnum,
    geographicLocation: GeographicLocationEnum,
    keyCompetenceTemplateIds: string[];
    greenCompetenceTemplateIds: string[];
    digitalCompetenceTemplateIds: string[];
    programProfessionalCompetencesValues: LearningCompetence[];
    programTransversalCompetencesValues: LearningCompetence[];
    programKeyCompetencesValues: LearningCompetence[];
    programGreenCompetencesValues: LearningCompetence[];
    programDigitalCompetencesValues: LearningCompetence[];
    learningKnowledgeOutcomeValues: LearningCompetence[];
    learningSkillOutcomeValues: LearningCompetence[];
    learningResponsabilityOutcomeValues: LearningCompetence[];
  }>();
  const { programId, studyCycle } = useParams<{ programId: string, studyCycle: StudyCycleEnum }>();
  const isDoctorate = studyCycle === StudyCycleEnum.Doctorate;
  const isBachelor = studyCycle === StudyCycleEnum.Bachelor;
  const canEditProgram = isAdmin || (isDirectorCsud && isDoctorate);
  const isDualProgram = Form.useWatch('isDualProgram', form);
  const colEcts = isDoctorate ? 6 : 3;
  const colAccreditation = isDoctorate ? 6 : 3;
  const colSupervisors = isDoctorate ? 24 : 12;
  const colIsced = isDoctorate ? 24 : (isDualProgram ? 24 : 20);

  const { data: programSupervisors, isLoading: isLoadingCourseSearch } = useQuery(['getAcademiUsersCourse', debouncedProgramSupervisorsSearch],
    () => {
        const response = getAllAcademicUsers(debouncedProgramSupervisorsSearch, 1, 10, undefined);
        return response;
    },
    {
        onError: openGetErrorNotification,
    }
  );

  const { data: program } = useQuery(["getProgram", programId], () => getStudyProgram(programId), {
    onError: (err) => {
      openGetErrorNotification(err);
    },
    onSuccess: (data) => {
      if (data) {
        form.setFieldsValue({
            name: data.name,
            // faculties: data.faculties,
            programNumber: data.programNumber,
            isDualProgram: data.isDualProgram,
            partnerName: data.partnerName,
            facultyId: data.facultyId,
            domainId: data.domainId,
            numberECTS: data.numberECTS,
            accreditationType: data.accreditationType,
            isEurAceCertified: data.isEurAceCertified ?? false,
            externalEvaluationHistory: data.externalEvaluationHistory,
            capacity: data.capacity,
            educaionalDomainISCED: data.educaionalDomainISCED,
            codCOR: data.codCOR,
            teachingFormat: data.teachingFormat,
            geographicLocation: data.geographicLocation,
            ancNoticeNumber: data.ancNoticeNumber,
            ancNoticeDate: moment(data.ancNoticeDate),
            aracisNoticeDate: moment(data.aracisNoticeDate),
            programSupervisor: data.programSupervisor,
            language: data.language,
            masterCategory: data.masterCategory,
            programSupervisorsIds: data.programSupervisors?.map((s: any) => s.id) || [],
        });        
      }
    },
    refetchOnWindowFocus: false
  });

  const { data: plans } = useQuery(
    ["getAllStudyPlansForProgramEditor", programId],
    () => getAllStudyPlans(programId, 1, 100),
    {
      enabled: !!programId,
      onError: openGetErrorNotification,
      refetchOnWindowFocus: false,
    }
  );

  const { data: competenceTemplates } = useQuery(
    ["getCompetenceTemplatesForStudyProgramForm"],
    () => getCompetenceTemplates(undefined, undefined, 1, 500),
    {
      enabled: !!programId && canEditProgram,
      onError: openGetErrorNotification,
      refetchOnWindowFocus: false
    }
  );

  const draftPlans = plans?.data?.filter(p => p.status === StudyPlanStatusEnum.Draft) ?? [];
  const hasDraftPlan = draftPlans.length > 0;
  // Fall back to all plans (any status) when the program has no draft plan, so the most
  // recent plan is still shown for reference instead of the page appearing empty.
  const selectablePlans = hasDraftPlan ? draftPlans : (plans?.data ?? []);
  const keyCompetenceTemplates = competenceTemplates?.data?.filter(e => e.type === LearningCompetenceTypeEnum.KeyCompetence) ?? [];
  const greenCompetenceTemplates = competenceTemplates?.data?.filter(e => e.type === LearningCompetenceTypeEnum.GreenCompetence) ?? [];
  const digitalCompetenceTemplates = competenceTemplates?.data?.filter(e => e.type === LearningCompetenceTypeEnum.DigitalCompetence) ?? [];

  useEffect(() => {
    if (!selectablePlans.length) {
      setSelectedDraftPlanId(undefined);
      form.setFieldsValue({
        codCOR: "",
        mission: "",
        objective: "",
        ocupationName: "",
        keyCompetenceTemplateIds: [],
        greenCompetenceTemplateIds: [],
        digitalCompetenceTemplateIds: [],
        programProfessionalCompetencesValues: [],
        programTransversalCompetencesValues: [],
        programKeyCompetencesValues: [],
        programGreenCompetencesValues: [],
        programDigitalCompetencesValues: [],
        learningKnowledgeOutcomeValues: [],
        learningSkillOutcomeValues: [],
        learningResponsabilityOutcomeValues: [],
      });
      return;
    }

    if (selectedDraftPlanId && selectablePlans.some(p => p.id === selectedDraftPlanId)) {
      return;
    }

    const latestPlan = [...selectablePlans].sort((a, b) => (b.planYear ?? 0) - (a.planYear ?? 0))[0];
    setSelectedDraftPlanId(latestPlan.id);
  }, [selectablePlans, selectedDraftPlanId]);

  useQuery(
    ["getSelectedDraftPlan", selectedDraftPlanId],
    () => getStudyPlanById(selectedDraftPlanId!),
    {
      enabled: !!selectedDraftPlanId,
      onError: openGetErrorNotification,
      onSuccess: (data) => {
        form.setFieldsValue({
          codCOR: data.codCOR ?? "",
          mission: data.mission ?? "",
          objective: data.objective ?? "",
          ocupationName: data.ocupationName ?? "",
          keyCompetenceTemplateIds: data.selectedKeyCompetenceTemplateIds || [],
          greenCompetenceTemplateIds: data.selectedGreenCompetenceTemplateIds || [],
          digitalCompetenceTemplateIds: data.selectedDigitalCompetenceTemplateIds || [],
          programProfessionalCompetencesValues: data.programProfessionalCompetences?.map(mapPlanLearningCompetence) || [],
          programTransversalCompetencesValues: data.programTransversalCompetences?.map(mapPlanLearningCompetence) || [],
          programKeyCompetencesValues: data.programKeyCompetences?.map(mapPlanLearningCompetence) || [],
          programGreenCompetencesValues: data.programGreenCompetences?.map(mapPlanLearningCompetence) || [],
          programDigitalCompetencesValues: data.programDigitalCompetences?.map(mapPlanLearningCompetence) || [],
          learningKnowledgeOutcomeValues: data.learningKnowledgeOutcome?.map(mapPlanLearningCompetence) || [],
          learningSkillOutcomeValues: data.learningSkillOutcome?.map(mapPlanLearningCompetence) || [],
          learningResponsabilityOutcomeValues: data.learningResponsabilityOutcome?.map(mapPlanLearningCompetence) || [],
        });
      },
      refetchOnWindowFocus: false,
    }
  );

const openFetchError = (_error: any) => {
  openNotification(
      'Eroare!',
      'Datele nu au putut fi aduse din server!',
      NOTIFICATION_TYPES.ERROR
  );
}

  const openSaveErrorNotification = async (error: any) => {
    const payload = await extractErrorPayload(error);
    const serverMessage =
      (typeof payload === "string" && payload) ||
      (typeof payload?.message === "string" && payload.message) ||
      (typeof error?.body === "string" && error.body) ||
      (typeof error?.body?.message === "string" && error.body.message) ||
      (typeof error?.response?.data === "string" && error.response.data) ||
      (typeof error?.response?.data?.message === "string" && error.response.data.message);
    const status = payload?.status ?? error?.status;

    if (serverMessage) {
      openNotification(
        'Eroare!',
        serverMessage,
        NOTIFICATION_TYPES.ERROR
      );
      return;
    }

    if (status === 400 || status === 409) {
      openNotification(
        'Eroare!',
        'Datele completate nu sunt valide!',
        NOTIFICATION_TYPES.ERROR
      );
    } else {
      openNotification(
        'Eroare!',
        'Propunerea nu a putut fi salvată! Contactati echipa de suport!',
        NOTIFICATION_TYPES.ERROR
      );
    }
  };

  const handleCancel = () => {
    setRedirect(true);
  };

  const openSuccessNotificationAndRedirect = () => {
    setRedirect(true);
    openNotification(
      'Succes!',
      'Salvarea s-a efectuat cu succes!',
      NOTIFICATION_TYPES.SUCCESS,
      6
    );
  }

  const faculty = Form.useWatch('facultyId', form);
  const selectedPlan = plans?.data?.find(p => p.id === selectedDraftPlanId);
  const isSelectedPlanDraft = selectedPlan?.status === StudyPlanStatusEnum.Draft;
  const canEditPlanInfo = (isAdmin || isProgramSupervisor(program?.programSupervisors || []) || (isDirectorCsud && isDoctorate)) && !!selectedDraftPlanId && isSelectedPlanDraft;
  const watchedKeyCompetences = Form.useWatch("programKeyCompetencesValues", form) || [];
  const watchedGreenCompetences = Form.useWatch("programGreenCompetencesValues", form) || [];
  const watchedDigitalCompetences = Form.useWatch("programDigitalCompetencesValues", form) || [];

  const buildTemplateOptions = (templates: any[], competences: LearningCompetence[]) => {
    const optionsById = new Map<string, string>();

    templates.forEach((template: any) => {
      if (template?.id) {
        optionsById.set(template.id, template.description || "");
      }
    });

    competences.forEach((competence) => {
      if (competence?.competenceTemplateId && !optionsById.has(competence.competenceTemplateId)) {
        optionsById.set(competence.competenceTemplateId, competence.description || "");
      }
    });

    return Array.from(optionsById.entries()).map(([id, description]) => ({ id, description }));
  };

  const keyTemplateOptions = buildTemplateOptions(keyCompetenceTemplates, watchedKeyCompetences);
  const greenTemplateOptions = buildTemplateOptions(greenCompetenceTemplates, watchedGreenCompetences);
  const digitalTemplateOptions = buildTemplateOptions(digitalCompetenceTemplates, watchedDigitalCompetences);

  const syncTemplateCompetenceList = (
    selectedTemplateIds: string[],
    formFieldName: "programKeyCompetencesValues" | "programGreenCompetencesValues" | "programDigitalCompetencesValues",
    templateType: LearningCompetenceTypeEnum
  ) => {
    const existingValues = (form.getFieldValue(formFieldName) || []) as LearningCompetence[];
    const existingByTemplateId = new Map(
      existingValues
        .filter((e) => !!e.competenceTemplateId)
        .map((e) => [e.competenceTemplateId as string, e])
    );

    const templatesById = new Map(
      (competenceTemplates?.data || [])
        .filter((e) => e.type === templateType && !!e.id)
        .map((e) => [e.id as string, e])
    );

    const nextValues: LearningCompetence[] = selectedTemplateIds.map((templateId) => {
      const existing = existingByTemplateId.get(templateId);
      if (existing) {
        return existing;
      }

      return {
        description: templatesById.get(templateId)?.description || "",
        type: templateType,
        isCompetenceTemplate: true,
        competenceTemplateId: templateId
      };
    });

    form.setFieldsValue({ [formFieldName]: nextValues } as any);
  };

  const { data: domains } = useQuery(['getDomains', faculty],
        () => {
            const state = form.getFieldsValue();
            return getDomains(undefined, state.facultyId === undefined ? undefined : [state.facultyId], studyCycle, 1, 100);
        },
        {
            onError: openFetchError,
        }
    );

    const { data: faculties } = useQuery(['getFaculties'],
        () => {
            return getFaculties(undefined, undefined, 1, 50);
        },
        {
            onError: openFetchError,
        }
    );


    const disabledFutureDate = (date: any) => {
      // Disable future dates
      return date > new Date();
    };

  const handleSave = () => {
    form.validateFields().then((state) => {
    if (state.name !== "" || (isDoctorate && programId)) {

      const learningCompetences = [
      ...(state.programProfessionalCompetencesValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.ProfessionalCompetence
      })),
      ...(state.programTransversalCompetencesValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.TransversalCompetence
      })),
      ...(state.programKeyCompetencesValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.KeyCompetence
      })),
      ...(state.programGreenCompetencesValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.GreenCompetence
      })),
      ...(state.programDigitalCompetencesValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.DigitalCompetence
      })),
      ...(state.learningKnowledgeOutcomeValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.Knowledge
      })),
      ...(state.learningSkillOutcomeValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.Skill
      })),
      ...(state.learningResponsabilityOutcomeValues || []).map(e => ({
        ...e,
        type: LearningCompetenceTypeEnum.ResponsibilityAutonomy
      })),
    ];

      let obj: StudyProgramUpdateDTO = {
        name: state.name,
        facultyId: state.facultyId,
        domainId: state.domainId,
        programNumber: state.programNumber,
        isDualProgram: !!state.isDualProgram,
        partnerName: state.isDualProgram ? (state.partnerName?.trim() || null) : null,
        numberECTS: state.numberECTS,
        accreditationType: state.accreditationType,
        externalEvaluationHistory: state.externalEvaluationHistory,
        capacity: state.capacity,
        educaionalDomainISCED: state.educaionalDomainISCED,
        codCOR: program?.codCOR ?? state.codCOR,
        teachingFormat: state.teachingFormat,
        geographicLocation: state.geographicLocation,
        ancNoticeNumber: state.ancNoticeNumber,
        ancNoticeDate: state.ancNoticeDate,
        aracisNoticeDate: state.aracisNoticeDate,
        studyCycle: studyCycle,
        programSupervisor: state.programSupervisor,
        language: state.language,
        masterCategory: state.masterCategory == null ? MasterCategoryEnum.NotApplied : state.masterCategory,
        programSupervisorsIds: state.programSupervisorsIds || [],
        learningCompetences: [],
        isEurAceCertified: state.isEurAceCertified ?? false,
      };

      if (isDoctorate) {
        const domainName = domains?.data?.find((d: any) => d.id === state.domainId)?.name ?? '';
        const facultyName = faculties?.find((f: any) => f.id === state.facultyId)?.name ?? '';
        obj.name = `Doctorat ${domainName} ${facultyName}`.replace(/\s+/g, ' ').trim();
        obj.language = StudyProgramLangEnum.Romana;
        obj.isEurAceCertified = false;
        obj.capacity = 0;
        obj.masterCategory = MasterCategoryEnum.NotApplied;
        obj.ancNoticeNumber = '0';
        obj.ancNoticeDate = moment('2100-01-01', 'YYYY-MM-DD').toDate() as any;
        obj.codCOR = program?.codCOR ?? state.codCOR ?? '';
      }

      if (!programId) {
        addStudyProgram(obj)
          .then(openSuccessNotificationAndRedirect)
          .catch(openSaveErrorNotification);
        return;
      }

      if (!hasDraftPlan || !selectedDraftPlanId) {
        openNotification(
          'Eroare!',
          'Nu există planuri cadru draft disponibile pentru editarea informațiilor de plan.',
          NOTIFICATION_TYPES.ERROR
        );
        return;
      }

      const planInfo = {
        codCOR: isDoctorate ? (state.codCOR ?? '') : state.codCOR,
        mission: state.mission,
        objective: state.objective,
        ocupationName: isDoctorate ? (state.ocupationName ?? '') : state.ocupationName,
        keyCompetenceTemplateIds: state.keyCompetenceTemplateIds || [],
        greenCompetenceTemplateIds: state.greenCompetenceTemplateIds || [],
        digitalCompetenceTemplateIds: state.digitalCompetenceTemplateIds || [],
        learningCompetences: learningCompetences,
      };

      updateStudyProgram(programId, obj)
        .then(() => updateStudyPlanInfo(selectedDraftPlanId, planInfo))
        .then(openSuccessNotificationAndRedirect)
        .catch(openSaveErrorNotification);
    } else {
      openNotification(
        'Eroare!',
        'Datele introduse nu sunt valide!',
        NOTIFICATION_TYPES.ERROR
      );
    }
    }).catch(() => {
      openNotification(
        'Eroare!',
        'Completați toate câmpurile obligatorii.',
        NOTIFICATION_TYPES.ERROR
      );
    });
  };
  
  const handleDeleteProgramCompetence = async (id: string, force = false) => {
    try {
      await deleteLearningCompetance(id, force);
    } catch (e: any) {
      throw e;
    }
  };


  if (redirect) {
    return <Redirect to={'/' + studyCycle + "/programe"} />;
  } else {
    return (
      <Layout>
        <div className={styles.container}>
          <CustomForm
            form={form}
            layout={theme.layout}
            action="/programe"
            boxshadow={"none"}
          >
            <div className={styles.firstLine}>    

              <Row gutter={8}>

                <Col span={12}>
                    <Form.Item
                      name='facultyId'
                      label={isDoctorate ? 'Școala doctorală:' : 'Facultate:'}
                      rules={[
                        {
                          required: true,
                          message: isDoctorate ? 'Selectarea școlii doctorale este obligatorie!' : 'Selectarea facultății este obligatorie!',
                          whitespace: false,
                          
                        },
                      ]}
                    >
                      <Select
                        allowClear
                        disabled={!canEditProgram}

                        style={{ width: "100%", textAlign: "left" }}
                        placeholder={isDoctorate ? 'Alege școala doctorală' : 'Alege facultatea'}
                      >
                        {
                          (faculties?.filter((f: any) => isDoctorate ? f.isDoctoralSchool : !f.isDoctoralSchool) ?? [])
                            .map((option: any, index) => (
                              <Option key={option.id}>{index + 1}. {option.name}</Option>
                          )) }
                        
                      </Select>
                    </Form.Item>
                </Col>

                 <Col span={12}>
                    <Form.Item
                      name='domainId'
                      label='Domeniu:'
                      rules={[
                        {
                          required: true,
                          message: 'Selectarea domeniului este obligatorie!',
                          whitespace: false,
                          
                        },
                      ]}
                    >
                      <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege domeniul'
                      >
                        {
                          domains?.data?.map((option: any) => (
                              <Option key={option.id}>{option.name}</Option>
                          )) ?? [] }
                      </Select>
                    </Form.Item>
                </Col>
                {!isDoctorate && (
                <Col span={12}>
                  <Form.Item
                    name='name'
                    label={isDoctorate ? 'Denumirea domeniului de studii:' : 'Denumirea programului de studii:'}
                    rules={[
                      {
                        required: true,
                        message: isDoctorate ? 'Denumirea domeniului de studii este obligatorie!' : 'Denumirea programului de studii este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditProgram}
                      rows={1}
                      cols={100}
                      placeholder={isDoctorate ? 'Completează denumirea domeniului de studii' : 'Completează denumirea programului de studii'}
                      />
                  </Form.Item>
                </Col>
                )}
                <Col span={6}>
                  <Form.Item
                    name='programNumber'
                    label={isDoctorate ? 'Număr domeniu:' : 'Număr program:'}
                    rules={[
                      {
                        required: true,
                        message: isDoctorate ? 'Numărul domeniului este obligatoriu!' : 'Numărul programului este obligatoriu!',
                      },
                    ]}
                  >
                    {isDoctorate ? (
                      <Input
                        disabled={!canEditProgram}
                        placeholder='Introduceți numărul domeniului'
                      />
                    ) : (
                    <textarea
                      disabled={!canEditProgram}
                      rows={1}
                      cols={100}
                      placeholder={isDoctorate ? 'Introduceți numărul domeniului' : 'Introduceți numărul programului'}
                      />
                    )}
                  </Form.Item>
                </Col>
                {!isDoctorate && (
                <Col span={6}>
                  <Form.Item
                    name='language'
                    label='Limba de predare:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea limbii de predare este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        defaultValue={StudyProgramLangEnum.Romana}
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege limba de predare'
                      >
                        <Option value={StudyProgramLangEnum.Romana}>Romană</Option>
                        <Option value={StudyProgramLangEnum.Engleza}>Engleză</Option>
                        <Option value={StudyProgramLangEnum.Franceza}>Franceză</Option>
                        <Option value={StudyProgramLangEnum.Germana}>Germană</Option>
                        <Option value={StudyProgramLangEnum.RoEn}>Română - Engleză</Option>
                        <Option value={StudyProgramLangEnum.EnFr}>Engleză - Franceză</Option>
                    </Select>
                  </Form.Item>
                </Col>
                )}

                <Col span={colEcts}>
                  <Form.Item
                    name='numberECTS'
                    label='Nr. ECTS:'
                    rules={[
                      {
                        required: true,
                        message: 'Nr. ECTS este obligatoriu!',
                        //whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Completează nr. ECTS'
                      >
                        {
                        studyCycle === StudyCycleEnum.Bachelor ?
                        [
                          <Option value='180'>180</Option>,
                          <Option value='240'>240</Option>
                        ] : studyCycle === StudyCycleEnum.Master ?
                        [
                          <Option value='60'>60</Option>,
                          <Option value='90'>90</Option>,
                          <Option value='120'>120</Option>,
                        ] :
                        [
                          <Option value='180'>180</Option>,
                          <Option value='240'>240</Option>
                        ]
                        }   
                    </Select>
                  </Form.Item>
                </Col>
                

                <Col span={colAccreditation}>
                  <Form.Item
                    name='accreditationType'
                    label='Acreditare:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea acreditarii este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege tipul acreditării'
                      >
                        <Option value={AccreditationTypeEnum.A}>A</Option>
                        <Option value={AccreditationTypeEnum.Ap}>AP</Option>
                        <Option value={AccreditationTypeEnum.Infiintare}>În curs de înființare</Option>
                        <Option value={AccreditationTypeEnum.Lichidare}>În curs de lichidare</Option>
                        
                    </Select>
                  </Form.Item>
                </Col>

                {isDoctorate && (
                <Col span={6}>
                  <Form.Item
                    name='aracisNoticeDate'
                    label='Data aviz ARACIS:'
                    rules={[
                      {
                        required: true,
                        message: 'Data aviz ARACIS este obligatorie!',
                        type: 'date'
                      },
                    ]}
                    >
                    <DatePicker
                      disabled={!canEditProgram}
                      placeholder='Completează data aviz ARACIS'
                      format={dateFormat}
                      disabledDate={disabledFutureDate}
                      style={{ width: '100%' }}
                      />
                  </Form.Item>
                </Col>
                )}

                {isDoctorate && (
                <Col span={12}>
                  <Form.Item
                    name='teachingFormat'
                    label='Forma de învățământ:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea formei de învățământ este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege forma de învățământ'
                      >
                        <Option value={TeachingFormatEnum.Frecventa}>Frecvență</Option>
                        <Option value={TeachingFormatEnum.FrecventaRedusa}>Frecvență redusă</Option>
                        <Option value={TeachingFormatEnum.Distanta}>Învățământ la distanță</Option>
                    </Select>
                  </Form.Item>
                </Col>
                )}

                {isDoctorate && (
                <Col span={12}>
                  <Form.Item
                    name='geographicLocation'
                    label='Locație geografică:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea locației geografice este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege locația geografică'
                      >
                        <Option value={GeographicLocationEnum.Bucuresti}>București</Option>
                        <Option value={GeographicLocationEnum.Pitesti}>Pitești</Option>
                        <Option value={GeographicLocationEnum.Alexandria}>Alexandria</Option>
                        <Option value={GeographicLocationEnum.Campulung}>Câmpulung</Option>
                        <Option value={GeographicLocationEnum.RamnicuValcea}>Râmnicu Vâlcea</Option>
                        <Option value={GeographicLocationEnum.Slatina}>Slatina</Option>
                    </Select>
                  </Form.Item>
                </Col>
                )}

                {!isDoctorate && (
                <Col span={2} style={{paddingLeft: 15}}>
                  <Form.Item
                    name='isEurAceCertified'
                    label='EUR-ACE:'
                    valuePropName='checked'
                    initialValue={false}
                  >
                    <Checkbox style={{paddingTop: 0}} disabled={!canEditProgram}>Da</Checkbox>
                  </Form.Item>
                </Col>
                )}
                

                {!isDoctorate && (
                <Col span={4}>
                  <Form.Item
                    name='capacity'
                    label='Capacitate de școlarizare:'
                    
                    rules={[{ required: true, pattern: /^\d+$/, message: "Capacitatea de școlarizare este obligatorie!" }]}
                    >
                      <InputNumber min={0} precision={0} style={{ width: "100%" }} disabled={!canEditProgram} />
                  </Form.Item>
                </Col>
                )}

                <Col span={colSupervisors}>
                <Form.Item
                    name="programSupervisorsIds"
                    label={isDoctorate ? 'Responsabili domeniu:' : 'Responsabili program:'}
                    rules={[
                      {
                        required: true,
                        message: isDoctorate ? 'Selectarea responsabililor de domeniu este obligatorie!' : 'Selectarea responsabililor de program este obligatorie!',
                      },
                    ]}
                  >
                    <Select
                        disabled={!canEditProgram}
                        className={styles.antSelect}
                        mode="multiple"
                        onSelect={() => setProgramSupervisorsSearchTerm('')}
                        maxTagCount={10}
                        allowClear
                        showSearch
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder={isDoctorate ? 'Căutare responsabili domeniu' : 'Căutare responsabili program'}
                        filterOption={false}
                        onSearch={setProgramSupervisorsSearchTerm}
                        notFoundContent={
                          isLoadingCourseSearch? <Spin size="small" /> : null
                        }
                    >
                        {[
                          ...(program?.programSupervisors || []),
                          ...(programSupervisors?.data?.filter(user => !(program?.programSupervisors || []).find(u => u.id === user.id)) || [])
                        ]
                            ?.map((user) => (
                                <Option key={user.id} value={user.id}>
                                    {user.name} - {user.email}
                                </Option>
                            ))
                            }
                    </Select>
                  </Form.Item>
                </Col>

                {!isDoctorate && (
                <Col span={4}>
                  <Form.Item
                    name='isDualProgram'
                    valuePropName='checked'
                    label='Program dual:'
                  >
                    <Checkbox style={{paddingTop: 0}} disabled={!canEditProgram}>Da</Checkbox>
                  </Form.Item>
                </Col>
                )}
                {isDualProgram && !isDoctorate && (
                  <Col span={20}>
                    <Form.Item
                      name='partnerName'
                      label='Nume operator economic:'
                      rules={[
                        {
                          required: true,
                          message: 'Denumirea operatorului economic este obligatorie pentru program dual!',
                          whitespace: true,
                        },
                      ]}
                    >
                      <Input
                        disabled={!canEditProgram}
                        placeholder='Introduceți denumirea operatorului economic'
                      />
                    </Form.Item>
                  </Col>
                )}

                <Col span={colIsced}>
                  <Form.Item
                    name='educaionalDomainISCED'
                    label='Domeniu educational ISCED:'
                    rules={[
                      {
                        required: true,
                        message: 'Domeniu educational ISCED 2013 este obligatoriu!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditProgram}
                      rows={1}
                      cols={100}
                      placeholder='Completează domeniu educational'
                      />
                  </Form.Item>
                </Col>
                

                <Col span={studyCycle === StudyCycleEnum.Master ? 6 : 0}>
                  <Form.Item
                    name='masterCategory'
                    label='Categorie Masterat:'
                    rules={[
                      {
                        required: studyCycle === StudyCycleEnum.Master,
                        message: 'Selectarea categoriei masteratului este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        defaultValue={MasterCategoryEnum.NotApplied}
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege categoria masteratului'
                      >
                        <Option value={MasterCategoryEnum.NotApplied}>Nu se aplică</Option>
                        <Option value={MasterCategoryEnum.Professional}>Profesional</Option>
                        <Option value={MasterCategoryEnum.Research}>De Cercetare</Option>
                        <Option value={MasterCategoryEnum.Teaching}>Didactic</Option>
                    </Select>
                  </Form.Item>
                </Col>

                <Col span={24}>
                  <Form.Item
                    name='externalEvaluationHistory'
                    label='Istoric evaluări externe:'
                    >
                    <textarea
                      disabled={!canEditProgram}
                      rows={1}
                      cols={100}
                      placeholder='Completează istoricul evaluărilor externe'
                      />
                  </Form.Item>
                </Col>      

                {!isDoctorate && (
                <Col span={8}>
                  <Form.Item
                    name='ancNoticeNumber'
                    label='Nr. aviz ANC:'
                    rules={[
                      {
                        required: true,
                        message: 'Nr. aviz ANC este obligatoriu!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditProgram}
                      rows={1}
                      cols={100}
                      placeholder='Completează nr. aviz ANC'
                      />
                  </Form.Item>
                </Col>
                )}

                {!isDoctorate && (
                <Col span={8}>
                  <Form.Item
                    name='ancNoticeDate'
                    label='Data aviz ANC:'
                    rules={[
                      {
                        required: true,
                        message: 'Data aviz ANC este obligatorie!',
                        type: 'date'
                      },
                    ]}
                    >
                    <DatePicker
                      disabled={!canEditProgram}
                      placeholder='Completează data aviz ANC'
                      format={dateFormat}
                      disabledDate={disabledFutureDate}/>
                  </Form.Item>
                </Col>
                )}

                {!isDoctorate && (
                <Col span={8}>
                  <Form.Item
                    name='aracisNoticeDate'
                    label='Data aviz ARACIS:'
                    rules={[
                      {
                        required: true,
                        message: 'Data aviz ARACIS este obligatorie!',
                        type: 'date'
                      },
                    ]}
                    >
                    <DatePicker
                      disabled={!canEditProgram}
                      placeholder='Completează data aviz ARACIS'
                      format={dateFormat}
                      disabledDate={disabledFutureDate}
                      />
                  </Form.Item>
                </Col>
                )}
               
                {!isDoctorate && (
                <Col span={12}>
                  <Form.Item
                    name='teachingFormat'
                    label='Forma de învățământ:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea formei de învățământ este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege forma de învățământ'
                      >
                        <Option value={TeachingFormatEnum.Frecventa}>Frecvență</Option>
                        <Option value={TeachingFormatEnum.FrecventaRedusa}>Frecvență redusă</Option>
                        <Option value={TeachingFormatEnum.Distanta}>Învățământ la distanță</Option>
                        
                    </Select>
                  </Form.Item>
                </Col>
                )}

                {!isDoctorate && (
                <Col span={12}>
                  <Form.Item
                    name='geographicLocation'
                    label='Locație geografică:'
                    rules={[
                      {
                        required: true,
                        message: 'Selectarea locației geografice este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <Select
                        disabled={!canEditProgram}
                        allowClear
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege locația geografică'
                      >
                        <Option value={GeographicLocationEnum.Bucuresti}>București</Option>
                        <Option value={GeographicLocationEnum.Pitesti}>Pitești</Option>
                        <Option value={GeographicLocationEnum.Alexandria}>Alexandria</Option>
                        <Option value={GeographicLocationEnum.Campulung}>Câmpulung</Option>
                        <Option value={GeographicLocationEnum.RamnicuValcea}>Râmnicu Vâlcea</Option>
                        <Option value={GeographicLocationEnum.Slatina}>Slatina</Option>
                        
                    </Select>
                  </Form.Item>
                </Col>
                )}

                {programId && (
                <>
                {programId && (
                  <Col span={24}>
                    <Form.Item
                      label='Plan cadru:'
                    >
                      <Select
                        disabled={(!canEditProgram && !isProgramSupervisor(program?.programSupervisors || [])) || !selectablePlans.length}
                        allowClear={false}
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege planul cadru'
                        value={selectedDraftPlanId}
                        onChange={(value) => setSelectedDraftPlanId(value)}
                      >
                        {selectablePlans
                          .sort((a, b) => (b.planYear ?? 0) - (a.planYear ?? 0))
                          .map((plan: any) => (
                            <Option key={plan.id} value={plan.id}>
                              {`Plan cadru ${plan.planYear}-${plan.endPlanYear}${plan.status !== StudyPlanStatusEnum.Draft ? ` (${plan.status})` : ''}`}
                            </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                )}

                {!isDoctorate && (
                <Col span={24}>
                  <Form.Item
                    name='codCOR'
                    label='Cod COR/ESCO:'
                    rules={[
                      {
                        required: !!programId && hasDraftPlan,
                        message: 'Codul COR este obligatoriu!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditPlanInfo}
                      rows={1}
                      cols={100}
                      placeholder='Completează codul COR'
                      />
                  </Form.Item>
                </Col>
                )}

                {!isDoctorate && (
                <Col span={24}>
                  <Form.Item
                    name='ocupationName'
                    label='Denumire ocupație:'
                    rules={[
                      {
                        required: !!programId && hasDraftPlan,
                        message: 'Denumirea ocupației este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditPlanInfo}
                      rows={2}
                      cols={100}
                      style={{minHeight: '80px', resize: 'vertical'}}
                      placeholder='Completează denumirea ocupației'
                      />
                  </Form.Item>
                </Col>
                )}
                
                <Col span={24}>
                  <Form.Item
                    name='mission'
                    label={
                       <div style={{textAlign: "left"}}>
                      {isDoctorate ? 'Misiunea domeniului de studii' : 'Misiunea programului de studii'}{' '}
                      <Tooltip overlayStyle={{ maxWidth: "400px" }} title={isDoctorate ? "Misiunea domeniului de studii trebuie să fie în concordanță cu misiunea Universității Naționale de Știință și Tehnologie POLITEHNICA București și cu cerințele identificate pe piața muncii." : "Misiunea programului de studii trebuie să fie în concordanță cu misiunea Universității Naționale de Știință și Tehnologie POLITEHNICA București și cu cerințele identificate pe piața muncii."}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </div>
                    }
                    rules={[
                      {
                        required: !!programId && hasDraftPlan,
                        message: isDoctorate ? 'Completarea misiunii domeniului de studii este obligatorie!' : 'Completarea misiunii programului de studii este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditPlanInfo}
                      rows={5}
                      cols={100}
                      style={{minHeight: '120px', resize: 'vertical'}}
                      placeholder={isDoctorate ? 'Completează misiunea domeniului de studii' : 'Completează misiunea programului de studii'}
                      />
                  </Form.Item>
                </Col>      


                <Col span={24}>
                  <Form.Item
                    name='objective'
                    label={
                       <div style={{textAlign: "left"}}>
                      {isDoctorate ? 'Obiectivele domeniului de studii' : 'Obiectivele programului de studii'}{' '}
                      <Tooltip overlayStyle={{ maxWidth: "400px" }} title={isDoctorate ? "Obiectivele domeniului de studii trebuie să fie în concordanță cu misiunea Universității Naționale de Știință și Tehnologie POLITEHNICA București și cu cerințele identificate pe piața muncii." : "Obiectivele programului de studii trebuie să fie în concordanță cu misiunea Universității Naționale de Știință și Tehnologie POLITEHNICA București și cu cerințele identificate pe piața muncii."}>
                        <InfoCircleOutlined style={{ color: '#1890ff' }} />
                      </Tooltip>
                    </div>
                    }
                    rules={[
                      {
                        required: !!programId && hasDraftPlan,
                        message: isDoctorate ? 'Completarea obiectivelor domeniului de studii este obligatorie!' : 'Completarea obiectivelor programului de studii este obligatorie!',
                        whitespace: true,
                      },
                    ]}
                    >
                    <textarea
                      disabled={!canEditPlanInfo}
                      rows={5}
                      cols={100}
                      style={{minHeight: '120px', resize: 'vertical'}}
                      placeholder={isDoctorate ? 'Completează obiectivele domeniului de studii' : 'Completează obiectivele programului de studii'}
                      />
                  </Form.Item>
                </Col>      

                <div style={{textAlign: "left"}}>
                  {isDoctorate ? 'Competențele formate în cadrul domeniului de studii' : 'Competențele formate în cadrul programului de studii'}{' '}
                  <Tooltip overlayStyle={{ maxWidth: "400px" }} title="„Competență” înseamnă capacitatea dovedită de a utiliza cunoștințe, aptitudini/ deprinderi și abilități personale, sociale și/sau metodologice în situații de muncă sau de studiu și pentru dezvoltarea profesională și personală">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </div>

                <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", paddingBottom: "16px" }}>
                  <div style={{ marginLeft: "20px", marginTop: "20px" }}>
                    a. Competențe profesionale{' '}
                    <Tooltip overlayStyle={{ maxWidth: "400px" }} title="Reprezintă capacitatea de a realiza activitățile solicitate la locul de muncă, fiind un ansamblu unitar și dinamic de cunoștințe, abilități și responsabilități">
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </div>
                  <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                    <ProgramCompetenceForm
                      name="programProfessionalCompetencesValues"
                      initialValue={[{ description: "", identificator: "1" }]}
                      rules={hasDraftPlan}
                      disabled={!canEditPlanInfo}
                      identifierPrefix="CP"
                      buttonText="Adaugă competență"
                      onRemoveSavedItem={handleDeleteProgramCompetence}
                      allowForceDelete={isAdmin}
                    />
                  </Col>

                  <div style={{ marginLeft: "20px", marginTop: "20px" }}>
                    b. Competențe transversale{' '}
                    <Tooltip overlayStyle={{ maxWidth: "400px" }} title={isDoctorate ? "Reprezintă achiziții valorice și atitudinale care depășesc un anumit domeniu de studiu și se exprimă prin următorii descriptori: autonomie și responsabilitate, interacțiune socială, dezvoltare personală și profesională." : "Reprezintă achiziții valorice și atitudinale care depășesc un anumit domeniu/program de studiu și se exprimă prin următorii descriptori: autonomie și responsabilitate, interacțiune socială, dezvoltare personală și profesională."}>
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </div>
                  <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                    <ProgramCompetenceForm
                      name="programTransversalCompetencesValues"
                      initialValue={[{ description: "", identificator: "1" }]}
                      rules={hasDraftPlan}
                      disabled={!canEditPlanInfo}
                      identifierPrefix="CT"
                      buttonText="Adaugă competență"
                      onRemoveSavedItem={handleDeleteProgramCompetence}
                      allowForceDelete={isAdmin}
                    />
                  </Col>

                  {isBachelor && (
                    <>
                      <div style={{ marginLeft: "20px", marginTop: "20px" }}>
                        c. Competențe cheie
                      </div>
                      <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                        <Form.Item
                          name="keyCompetenceTemplateIds"
                        >
                          <Select
                            mode="multiple"
                            disabled={!canEditPlanInfo}
                            allowClear
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder="Alege competențele cheie"
                            onChange={(value) => syncTemplateCompetenceList(value || [], "programKeyCompetencesValues", LearningCompetenceTypeEnum.KeyCompetence)}
                          >
                            {keyTemplateOptions.map((template: any) => (
                              <Option key={template.id} value={template.id}>
                                {template.description}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <ProgramCompetenceForm
                          name="programKeyCompetencesValues"
                          initialValue={[]}
                          rules={false}
                          disabled={true}
                          identifierPrefix="CC"
                          buttonText="Adaugă competență"
                        />
                      </Col>

                      <div style={{ marginLeft: "20px", marginTop: "20px" }}>
                        d. Competențe verzi
                      </div>
                      <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                        <Form.Item
                          name="greenCompetenceTemplateIds"
                        >
                          <Select
                            mode="multiple"
                            disabled={!canEditPlanInfo}
                            allowClear
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder="Alege competențele verzi"
                            onChange={(value) => syncTemplateCompetenceList(value || [], "programGreenCompetencesValues", LearningCompetenceTypeEnum.GreenCompetence)}
                          >
                            {greenTemplateOptions.map((template: any) => (
                              <Option key={template.id} value={template.id}>
                                {template.description}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <ProgramCompetenceForm
                          name="programGreenCompetencesValues"
                          initialValue={[]}
                          rules={false}
                          disabled={true}
                          identifierPrefix="CV"
                          buttonText="Adaugă competență"
                        />
                      </Col>

                      <div style={{ marginLeft: "20px", marginTop: "20px" }}>
                        e. Competențe digitale
                      </div>
                      <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                        <Form.Item
                          name="digitalCompetenceTemplateIds"
                        >
                          <Select
                            mode="multiple"
                            disabled={!canEditPlanInfo}
                            allowClear
                            style={{ width: "100%", textAlign: "left" }}
                            placeholder="Alege competențele digitale"
                            onChange={(value) => syncTemplateCompetenceList(value || [], "programDigitalCompetencesValues", LearningCompetenceTypeEnum.DigitalCompetence)}
                          >
                            {digitalTemplateOptions.map((template: any) => (
                              <Option key={template.id} value={template.id}>
                                {template.description}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <ProgramCompetenceForm
                          name="programDigitalCompetencesValues"
                          initialValue={[]}
                          rules={false}
                          disabled={true}
                          identifierPrefix="CD"
                          buttonText="Adaugă competență"
                        />
                      </Col>
                    </>
                  )}
                </Row>

                <div style={{ textAlign: "left", marginTop: "20px" }}>
                  {isDoctorate ? 'Rezultatele învățării formate în cadrul domeniului de studii' : 'Rezultatele învățării formate în cadrul programului de studii'}{' '}
                  <Tooltip overlayStyle={{ maxWidth: "400px" }} title="Sunt enunțuri sintetice referitoare la ceea ce un student va fi capabil să facă sau să demonstreze la finalizarea unui curs. Rezultatele învățării reflectă realizările studentului și mai puțin intențiile profesorului. Rezultatele învățării informează studenții despre ceea ce se așteaptă de la ei din punct de vedere al performanței, pentru a obține notele și creditele dorite. Sunt definite în termeni concreți, folosind verbe similare exemplelor de mai jos și indică ceea ce se va urmări prin evaluare">
                    <InfoCircleOutlined style={{ color: '#1890ff' }} />
                  </Tooltip>
                </div>

                <Row gutter={16} style={{ borderBottom: "1px solid lightgrey", paddingTop: "25px", paddingBottom: "16px" }}>
                  <div style={{ textAlign: "left", marginLeft: "20px", marginTop: "20px" }}>
                    a. Cunoștințe{' '}
                    <Tooltip overlayStyle={{ maxWidth: "400px" }} title="Rezultatul asimilării de informații prin învățare. Cunoștințele reprezintă ansamblul de fapte, principii, teorii și practici legate de un anumit domeniu de muncă sau de studiu. Pot fi teoretice și/sau faptice">
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </div>
                  <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                    <ProgramCompetenceForm
                      name="learningKnowledgeOutcomeValues"
                      initialValue={[{ description: "", identificator: "1" }]}
                      rules={hasDraftPlan}
                      disabled={!canEditPlanInfo}
                      identifierPrefix="C"
                      buttonText="Adaugă rezultat"
                      onRemoveSavedItem={handleDeleteProgramCompetence}
                      allowForceDelete={isAdmin}
                    />
                  </Col>

                  <div style={{ textAlign: "left", marginLeft: "20px", marginTop: "20px" }}>
                    b. Abilități{' '}
                    <Tooltip overlayStyle={{ maxWidth: "400px" }} title="Capacitatea de a aplica cunoștințe și de a utiliza know-how pentru a duce la îndeplinire sarcini și a rezolva probleme. Aptitudinile/ deprinderile sunt descrise ca fiind cognitive (implicând utilizarea gândirii logice, intuitive și creative) sau practice (implicând dexteritate manuală și utilizarea de metode, materiale, unelte și instrumente)">
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </div>
                  <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                    <ProgramCompetenceForm
                      name="learningSkillOutcomeValues"
                      initialValue={[{ description: "", identificator: "1" }]}
                      rules={hasDraftPlan}
                      disabled={!canEditPlanInfo}
                      identifierPrefix="A"
                      buttonText="Adaugă rezultat"
                      onRemoveSavedItem={handleDeleteProgramCompetence}
                      allowForceDelete={isAdmin}
                    />
                  </Col>

                  <div style={{ textAlign: "left", marginLeft: "20px", marginTop: "20px" }}>
                    c. Responsabilitate și Autonomie{' '}
                    <Tooltip overlayStyle={{ maxWidth: "400px" }} title="Înseamnă capacitatea cursantului de a aplica în mod autonom și responsabil cunoștințele și aptitudinile sale">
                      <InfoCircleOutlined style={{ color: '#1890ff' }} />
                    </Tooltip>
                  </div>
                  <Col span={24} style={{ padding: "8px 0px 0px 12px" }}>
                    <ProgramCompetenceForm
                      name="learningResponsabilityOutcomeValues"
                      initialValue={[{ description: "", identificator: "1" }]}
                      rules={hasDraftPlan}
                      disabled={!canEditPlanInfo}
                      identifierPrefix="RA"
                      buttonText="Adaugă rezultat"
                      onRemoveSavedItem={handleDeleteProgramCompetence}
                      allowForceDelete={isAdmin}
                    />
                  </Col>
                </Row>
                </>
                )}

                {/* select programe de studii */}
                {/* <Col span={24}>
                    <Form.Item
                      name='studyPrograms'
                      label='Programe de studiu asociate:'
                    >
                      <Select
                        allowClear
                        mode="multiple"
                        style={{ width: "100%", textAlign: "left" }}
                        placeholder='Alege programele de studiu asociate'
                      >
                        <Option value='M_CTI'>Calculatoare si tehnologia informţiei</Option>
                        <Option value='M_Auto'>Ingineria autovehiculelor</Option>
                        <Option value='M_Materiale'>Ingineria materialelor</Option>
                        <Option value='M_Mediu'>Ingineria mediului</Option>
                        <Option value='M_Alimente'>Ingineria produselor alimentare</Option>
                        <Option value='M_Sisteme'>Ingineria sistemelor</Option>
                        <Option value='M_Transporturi'>Ingineria transporturilor</Option>
                        <Option value='M_Aerospatiala'>Inginerie aerospaţială</Option>
                        <Option value='M_Chimica'>Inginerie chimică</Option>
                        <Option value='M_Electrica'>Inginerie electrică</Option>
                        <Option value='M_ETTI'>Inginerie electronică, telecomunicaţii și tehnologii informaționale</Option>
                        <Option value='M_Energetica'>Inginerie energetică</Option>
                        <Option value='M_Industriala'>Inginerie industrială</Option>
                        <Option value='M_Mecanica'>Inginerie mecanică</Option>
                        <Option value='M_Management'>Inginerie şi management</Option>
                        <Option value='M_Mecatronica'>Mecatronică şi robotică</Option>
                        <Option value='M_Aplicate'>Ştiinţe inginereşti aplicate</Option>
                        <Option value='M_Comunicare'>Stiințe ale comunicării</Option>
                        <Option value='M_Relatii'>Relații internaționale și studii europene</Option>
                        <Option value='M_Economie'>Economie</Option>
                        <Option value='M_Educatie'>Științe ale educației</Option>
                      </Select>
                    </Form.Item>
                </Col> */}
              </Row>
            </div>
            <div className={styles.secondLine}>
              <div className={styles.firstColumn}>
                <ConfirmationModal
                  nameButton={t('internships.addInternshipForm.cancel')}
                  modalText={t('internships.cancelMessage')}
                  titleButton={''}
                  handleFunction={handleCancel}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.green, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
                <ConfirmationModal
                  nameButton={programId ? t('internships.addInternshipForm.updateButton') : t('internships.addInternshipForm.saveButton')}
                  modalText={t('internships.saveMessage')}
                  titleButton={''}
                  handleFunction={handleSave}
                  iconButton={null}
                  shapeButton={''}
                  typeButton={''}
                  disabledProp={false}
                  styleProp={{ background: theme.secondColor, color: theme.white, marginRight: '10%', marginTop: '0%', boxShadow: 'rgba(0, 0, 0, 0.1) 0px 4px 12px' }}
                />
              </div>
            </div>
          </CustomForm>
        </div>
      </Layout>
    );
  }
};

export default StudyProgramForm;
